import useGridUrlParams from 'hooks/useGridUrlParams';
import Filter from 'models/api/filter';
import {
    createContext,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

export type WorkplaceFilterValue =
    | string
    | number
    | boolean
    | string[]
    | number[]
    | undefined
    | null;

export type WorkplaceFilterTypeItem<ItemType extends WorkplaceFilterValue = WorkplaceFilterValue> =
    | {
          value: ItemType;
          urlIgnore?: boolean;
          array?: boolean;
          parse?: (val: string) => ItemType;
      }
    | ItemType;

export type WorkplaceFilterType<FilterType> = Filter<{
    [P in keyof FilterType]: FilterType[P] extends WorkplaceFilterValue
        ? WorkplaceFilterTypeItem<FilterType[P]>
        : never;
}>;

const mutateWorkplaceFilterType = <FilterType,>(wpf: WorkplaceFilterType<FilterType>) => {
    const result = {} as any;
    for (const keyString in wpf) {
        const key = keyString as keyof WorkplaceFilterType<FilterType>;
        const item = wpf[key] as any;
        const value = item instanceof Object ? item.value : item;
        result[key] = value;
    }

    return result as FilterType;
};

type GridFilterContextType<FilterType extends Filter> = {
    draftFilter: FilterType;
    setDraftFilter: Dispatch<SetStateAction<FilterType>>;
    resetDraftFilter: (filter?: Partial<FilterType>) => void;
    filter: FilterType;
    setFilter: (param: SetStateAction<FilterType>) => void;
    resetFilter: (filter?: Partial<FilterType>) => void;
};

const GridFilterContext = createContext<GridFilterContextType<any>>(
    {} as GridFilterContextType<any>
);

export type GridFilterProviderType<FilterType extends Filter> = React.PropsWithChildren<{
    filterParams: WorkplaceFilterType<FilterType>;
    defaultPageSize?: number;
    urlFilterKeyPrefix?: string;
    fixUrlFilter?: (filter: FilterType) => FilterType;
}>;

export const GridFilterUrlProvider = <FilterType extends Filter>({
    children,
    defaultPageSize = 25,
    filterParams: defaultFilterParams,
    urlFilterKeyPrefix,
    fixUrlFilter,
}: GridFilterProviderType<FilterType>) => {
    const initialFilter: Filter = {
        pageNum: 1,
        pageSize: defaultPageSize,
        orderDesc: false,
        orderColumn: '',
    };

    const initialFilterState: FilterType = useMemo(
        () => ({
            ...initialFilter,
            ...mutateWorkplaceFilterType<FilterType>(defaultFilterParams),
        }),
        [initialFilter, defaultFilterParams]
    );

    const [filter, setFilter] = useGridUrlParams<FilterType>(
        initialFilterState,
        defaultFilterParams,
        urlFilterKeyPrefix,
        fixUrlFilter
    );

    const [draftFilter, setDraftFilter] = useState<FilterType>(filter);

    useEffect(() => {
        setDraftFilter(filter);
    }, [filter]);

    return (
        <GridFilterContext.Provider
            value={{
                draftFilter,
                setDraftFilter,
                resetDraftFilter: (filter) =>
                    setDraftFilter({ ...initialFilterState, ...(filter ? filter : {}) }),
                filter,
                setFilter,
                resetFilter: (filter) =>
                    setFilter({ ...initialFilterState, ...(filter ? filter : {}) }),
            }}
        >
            {children}
        </GridFilterContext.Provider>
    );
};

const useGridFilterUrlContext = <FilterType extends Filter>() => {
    const context = useContext<GridFilterContextType<FilterType>>(GridFilterContext);

    return context;
};

export { useGridFilterUrlContext };
